import { createContext, ReactNode, useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useRoom } from '../hooks/useRoom';
import { useGameManager } from '../hooks/useGameManager';
import { useNotice } from '../hooks/useNotice';
import { usePlatformAdmin } from '../hooks/usePlatformAdmin';
import { useRoomConnection } from '../hooks/useRoomConnection';

export interface AppContextType {
  auth: ReturnType<typeof useAuth>;
  room: ReturnType<typeof useRoom>;
  game: ReturnType<typeof useGameManager>;
  roomConnection: ReturnType<typeof useRoomConnection>;
  /** Whether the superadmin console is the view on screen (§8). */
  platformAdmin: ReturnType<typeof usePlatformAdmin>;
  /**
   * One line of news about the session itself. It lives here because it
   * outlives what it is about: the island it names has been left, and in the
   * deactivation case the session has ended too.
   */
  notice: ReturnType<typeof useNotice>;
}

export const AppContext = createContext<AppContextType | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
}

export function AppProvider({ children }: AppProviderProps) {
  const auth = useAuth();
  const room = useRoom();
  const game = useGameManager();
  const notice = useNotice();
  const roomConnection = useRoomConnection(auth, room, notice);
  const platformAdmin = usePlatformAdmin(auth.isAuthenticated);

  const contextValue: AppContextType = useMemo(() => ({
    auth,
    room,
    game,
    roomConnection,
    platformAdmin,
    notice,
  }), [auth, room, game, roomConnection, platformAdmin, notice]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
}